import { catchError, takeUntil } from 'rxjs/operators';
import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { DataService } from '@adscore/adscore-frontend-common';
import { NEVER_TIMEOUT, LogLevel, LogService } from '@adscore/adscore-frontend-common';
import { SwalComponent, SwalPortalTargets, SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { UserService } from '@adscore/adscore-frontend-common';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  imports: [SweetAlert2Module],
  standalone: true
})
export class PasswordResetComponent implements OnInit, AfterViewInit, OnDestroy {
  uid: string;
  token: string;
  passwordReset$: Subscription | null;

  @ViewChild('confirmResetPasswordSwal', { static: true })
  private confirmResetPasswordSwal: SwalComponent;

  private destroy$ = new Subject<boolean>();

  constructor(
    private dataService: DataService,
    private logService: LogService,
    private userService: UserService,
    public readonly swalTargets: SwalPortalTargets
  ) {}

  ngOnInit() {
    this.uid = this.dataService.get('password-reset-uid');
    this.token = this.dataService.get('password-reset-token');
  }

  ngAfterViewInit(): void {
    this.confirmResetPasswordSwal.fire().then((result) => {
      if (!result.isConfirmed) {
        if (this.passwordReset$) {
          this.passwordReset$.unsubscribe();
        }
        return;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  preConfirmResetPassword = () => {
    this.onConfirmPasswordReset();
    return false;
  };

  onConfirmPasswordReset() {
    // methods
    const isBlank = (x: string) => x.replace(/[ \t\n]/g, '').length === 0;
    const removeErrorStyle = (el: HTMLElement): void => {
      el.classList.remove('b-primary');
      el.classList.add('b-secondary-lighter');
    };
    const addErrorStyle = (el: HTMLElement): void => {
      el.classList.add('b-primary');
      el.classList.remove('b-secondary-lighter');
    };

    // working data
    const tag = 'password-reset';
    const passwordEl = <HTMLInputElement>document?.getElementById('new-password');
    const passwordConfirmationEl = <HTMLInputElement>document?.getElementById('new-password-confirmation');
    const password = passwordEl.value;
    const passwordConfirmation = passwordConfirmationEl.value;
    const errors = [];

    removeErrorStyle(passwordEl);
    removeErrorStyle(passwordConfirmationEl);

    if (isBlank(password)) {
      errors.push('Please fill in your Password');
      addErrorStyle(passwordEl);
    } else if (password.length < 8) {
      errors.push('Make sure your Password is at least 8 characters long.');
      addErrorStyle(passwordConfirmationEl);
    }
    if (password !== passwordConfirmation) {
      errors.push('Make sure your Password Confirmation matches your Password');
      addErrorStyle(passwordConfirmationEl);
    }

    if (errors.length) {
      this.logService.addMessage(
        `Password Reset`,
        `<ul>${errors.map((msg) => `<li>${msg}</li>`).join('')}</ul>`,
        LogLevel.Error,
        0,
        tag
      );
      return;
    }

    // submit new password if it passed validation
    this.passwordReset$ = this.userService
      .resetPassword(this.uid, this.token, password)
      .pipe(
        catchError((apiErrors) => {
          this.passwordReset$ = null;
          return this.logService.notifyUserAboutErrors(apiErrors);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.passwordReset$ = null;
        this.confirmResetPasswordSwal.close();
        this.logService.addMessage(
          `Password Reset`,
          `Your password was successfully reset!<br>Please login using your e-mail and new password.`,
          LogLevel.Success,
          NEVER_TIMEOUT,
          tag
        );
      });
  }

  onKeyDown(event: KeyboardEvent, selector: string) {
    this.fixTabBehavior(event, selector);
  }

  private fixTabBehavior(event: KeyboardEvent, selector: string) {
    if (event.keyCode !== 9) {
      return;
    }

    const elementToFocus = document?.querySelector(selector);
    if (elementToFocus) {
      (elementToFocus as HTMLElement).focus();
    }
    event.preventDefault();
    event.stopPropagation();
  }
}
